import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Briefcase,
  Coins,
  Loader2,
  Lock,
  Pencil,
  Shield,
  Trash2,
  Users,
} from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAttachPermission, useDetachPermission } from '../hooks/use-roles'
import {
  categorizePermissions,
  getPermissionAction,
  type Permission,
  type Role,
} from '../types'

interface PermissionsMatrixProps {
  roles: Role[]
  allPermissions: Permission[]
  isLoading?: boolean
  onEditRole?: (role: Role) => void
  onDeleteRole?: (role: Role) => void
  canUpdatePermissions?: boolean
}

const categoryIcons: Record<string, React.ReactNode> = {
  USER: <Users className='h-4 w-4' />,
  ROLE: <Shield className='h-4 w-4' />,
  ENGINE: <Briefcase className='h-4 w-4' />,
  INVESTOR: <Coins className='h-4 w-4' />,
}

const actionColors: Record<string, string> = {
  READ: 'hsl(var(--chart-1))',
  CREATE: 'hsl(var(--chart-2))',
  UPDATE: 'hsl(var(--chart-3))',
  DELETE: 'hsl(var(--chart-4))',
}

export function PermissionsMatrix({
  roles,
  allPermissions,
  isLoading,
  onEditRole,
  onDeleteRole,
  canUpdatePermissions = true,
}: PermissionsMatrixProps) {
  const { t } = useTranslation()
  const attachPermission = useAttachPermission()
  const detachPermission = useDetachPermission()
  const [loadingKey, setLoadingKey] = React.useState<string | null>(null)

  const categories = categorizePermissions(allPermissions)



  const handlePermissionToggle = (
    roleId: string,
    permission: string,
    isChecked: boolean
  ) => {
    // Check if user has permission to update and if role allows updates
    if (!canUpdatePermissions) {
      return
    }

    const role = roles.find((r) => r.id === roleId)
    if (!role?.updatesAllowed) {
      return
    }

    const key = `${roleId}-${permission}`
    setLoadingKey(key)

    if (isChecked) {
      detachPermission.mutate(
        { roleId, permission },
        {
          onSettled: () => setLoadingKey(null),
        }
      )
    } else {
      attachPermission.mutate(
        { roleId, permission },
        {
          onSettled: () => setLoadingKey(null),
        }
      )
    }
  }

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-full' />
        <Skeleton className='h-64 w-full' />
      </div>
    )
  }

  if (roles.length === 0) {
    return (
      <div className='flex h-64 items-center justify-center rounded-lg border border-dashed'>
        <p className='text-muted-foreground text-sm'>{t('roles.noRoles')}</p>
      </div>
    )
  }

  return (
    <ScrollArea dir={t('dir') as 'ltr' | 'rtl'} className='w-full rounded-lg border whitespace-nowrap'>
      <Table dir={t('dir') as 'ltr' | 'rtl'}>
        <TableHeader>
          <TableRow className='bg-muted/50'>
            <TableHead className='sticky start-0 z-20 font-semibold bg-background'>
              {t('roles.actions')}
            </TableHead>
            {roles.map((role) => (
              <TableHead key={role.id} className='min-w-[120px]'>
                <div className='flex flex-col items-center justify-center gap-2 py-2'>
                  <div className='flex items-center gap-1.5'>
                    <span className='font-semibold'>{role.name}</span>
                    {!role.updatesAllowed && (
                      <Lock className='size-3 opacity-70' />
                    )}
                  </div>

                  {role.updatesAllowed && (
                    <div className='flex items-center gap-2'>

                      <div className='flex items-center gap-0.5 border-s ps-2 ms-1'>
                        {onEditRole && (
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-6 w-6'
                            onClick={() => onEditRole(role)}
                          >
                            <Pencil className='h-3.5 w-3.5' />
                            <span className='sr-only'>Edit {role.name}</span>
                          </Button>
                        )}
                        {onDeleteRole && (
                          <Button
                            variant='ghost'
                            size='icon'
                            className='text-destructive hover:text-destructive h-6 w-6'
                            onClick={() => onDeleteRole(role)}
                          >
                            <Trash2 className='h-3.5 w-3.5' />
                            <span className='sr-only'>Delete {role.name}</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.map((category) => (
            <React.Fragment key={category.name}>
              {/* Category Header */}
              <TableRow className='bg-muted/30'>
                <TableCell
                  colSpan={roles.length + 1}
                  className='bg-muted/30 sticky start-0 z-10 font-semibold'
                >
                  <div className='flex items-center gap-2'>
                    {categoryIcons[category.name]}
                    <span>
                      {t(`roles.categories.${category.name.toLowerCase()}`)}
                    </span>
                  </div>
                </TableCell>
              </TableRow>

              {/* Permissions for this category */}
              {category.permissions.map((permission) => {
                const action = getPermissionAction(permission)
                const actionColor =
                  actionColors[action] || 'hsl(var(--foreground))'

                return (
                  <TableRow key={permission.id}>
                    <TableCell className='bg-background sticky start-0 z-10 font-medium border-e shadow-[1px_0_0_0_rgba(0,0,0,0.1)] ltr:shadow-[1px_0_0_0_rgba(0,0,0,0.1)] rtl:shadow-[-1px_0_0_0_rgba(0,0,0,0.1)]'>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-sm'>
                          {t(
                            `roles.permissions.${permission.full_codename}`,
                            permission.name
                          )}
                        </span>
                        <Badge
                          variant='outline'
                          style={{
                            borderColor: actionColor,
                            color: actionColor,
                          }}
                          className='text-xs'
                        >
                          {t(`permission_actions.${action}`, action)}
                        </Badge>
                      </div>
                    </TableCell>

                    {roles.map((role) => {
                      const isChecked = role.permissions.includes(permission.full_codename) // Role permissions are strings
                      const isDisabled =
                        !canUpdatePermissions || !role.updatesAllowed
                      const key = `${role.id}-${permission.full_codename}`
                      const isLoadingThisPermission = loadingKey === key

                      return (
                        <TableCell key={key} className='text-center'>
                          <div className='flex justify-center'>
                            {isLoadingThisPermission ? (
                              <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
                            ) : (
                              <Checkbox
                                checked={isChecked}
                                disabled={isDisabled || loadingKey !== null}
                                onCheckedChange={() =>
                                  handlePermissionToggle(
                                    role.id,
                                    permission.full_codename,
                                    isChecked
                                  )
                                }
                                className='data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground h-5 w-5'
                              />
                            )}
                          </div>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      <ScrollBar orientation='horizontal' />
    </ScrollArea>
  )
}
